import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './Card';
import { contractTitle } from '../utils/contractTitle';
import { ContractList, type ContractListRow, type ContractSortField } from './ContractList';
import { ContractHistory } from './ContractHistory';
import { DocumentIntegrityModal } from './DocumentIntegrityModal';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreviewModal';
import { contractVersionBadge, draftStatusBadge } from '../utils/contractBadges';
import { contractApi, documentApi } from '../services/api';
import type {
  Contract,
  Milestone,
  DocumentRecord,
  ContractHistoryEntry
} from '../types';

interface ProviderContractsTabProps {
  projectId: string;
  contracts: Contract[];
  contractMilestonesMap: Record<string, Milestone[]>;
}

const formatAmount = (amount: string | number): string => {
  const num =
    typeof amount === 'string'
      ? parseFloat(amount.replace(/[^0-9.-]/g, '') || '0')
      : amount;
  return new Intl.NumberFormat('sv-SE').format(Number.isFinite(num) ? num : 0);
};

const formatDate = (date?: string | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('sv-SE');
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const attestationVariant = (status?: string): string => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'secondary';
  }
};

/**
 * Read-only contract list + details for the provider roles (jurist /
 * besiktningsman). Reuses the company design language via the shared
 * ContractList, badge utilities, ContractHistory and document preview — but
 * exposes no create/edit/sign/upload actions. The provider must be able to
 * assess exactly the same contract material the parties acted on, without
 * being able to change anything. Backend authorization is the real gate; this
 * view simply omits every write affordance.
 */
export function ProviderContractsTab({
  projectId,
  contracts,
  contractMilestonesMap
}: ProviderContractsTabProps) {
  const { t } = useTranslation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<ContractSortField>('contractNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [descriptions, setDescriptions] = useState<{
    assignment: string;
    other: string;
    referToAttachments: boolean;
  } | null>(null);
  const [attachments, setAttachments] = useState<DocumentRecord[]>([]);
  const [history, setHistory] = useState<ContractHistoryEntry[]>([]);
  const [attachmentDetailsTarget, setAttachmentDetailsTarget] =
    useState<DocumentRecord | null>(null);

  const preview = useDocumentPreview();

  // Auto-select the first contract, mirroring the company view. Keeps the
  // selection valid if the contract set changes.
  useEffect(() => {
    if (contracts.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((current) =>
      current && contracts.some((c) => c.id === current) ? current : contracts[0].id
    );
  }, [contracts]);

  const selectedContract = useMemo(
    () => contracts.find((c) => c.id === selectedId) ?? null,
    [contracts, selectedId]
  );

  // Load the read-only detail material for the selected contract.
  useEffect(() => {
    if (!selectedContract) {
      setDescriptions(null);
      setAttachments([]);
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [desc, docs, hist] = await Promise.all([
          contractApi.getContractDescriptions(selectedContract.id).catch(() => null),
          documentApi
            .listDocuments(projectId, selectedContract.contractNumber)
            .catch(() => []),
          contractApi.getHistory(selectedContract.id).catch(() => [])
        ]);
        if (cancelled) return;
        setDescriptions(
          desc
            ? {
                assignment: desc.assignmentDescription?.content || '',
                other: desc.other?.content || '',
                referToAttachments: desc.referToAttachments?.content === 'true'
              }
            : null
        );
        setAttachments(docs);
        setHistory(hist);
      } catch {
        if (cancelled) return;
        setDescriptions(null);
        setAttachments([]);
        setHistory([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedContract, projectId]);

  const handleSort = (field: ContractSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const typeLabelFor = (contract: Contract): string =>
    contract.contractType === 'variation'
      ? `${t('contracts.types.variation')}${contract.variationNumber != null ? ` #${contract.variationNumber}` : ''}`
      : t('contracts.types.fixed');

  const rows: ContractListRow[] = useMemo(() => {
    const sorted = [...contracts].sort((a, b) => {
      let aVal: unknown = (a as unknown as Record<string, unknown>)[sortField];
      let bVal: unknown = (b as unknown as Record<string, unknown>)[sortField];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      aVal = String(aVal ?? '').toLowerCase();
      bVal = String(bVal ?? '').toLowerCase();
      if ((aVal as string) < (bVal as string)) return sortDirection === 'asc' ? -1 : 1;
      if ((aVal as string) > (bVal as string)) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted.map((contract) => {
      const original = contract.replacesContractId
        ? contracts.find((candidate) => candidate.id === contract.replacesContractId)
        : undefined;
      return {
        id: contract.id,
        contractNumber: contract.contractNumber,
        typeLabel: typeLabelFor(contract),
        isVariation: contract.contractType === 'variation',
        description: contract.contractName || t('contracts.list.contractName', { number: contract.contractNumber }),
        replacesLabel: original
          ? `Ersätter kontrakt #${original.contractNumber}, avtalsversion ${original.contractVersion ?? 1} – ${contractTitle(original)}`
          : null,
        etappCount: (contractMilestonesMap[contract.id] || []).length,
        amountLabel: formatAmount(contract.contractAmount),
        paidLabel: formatAmount(contract.paidAmount),
        remainingLabel: formatAmount(contract.remainingAmount),
        versionBadge: contractVersionBadge(contract),
        draftBadge: draftStatusBadge(contract.draftStatus, t),
        danger: contract.draftStatus === 'draft_rejected'
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, contractMilestonesMap, sortField, sortDirection]);

  const milestones = selectedContract
    ? contractMilestonesMap[selectedContract.id] || []
    : [];

  const parentContract =
    selectedContract?.parentContractId
      ? contracts.find((c) => c.id === selectedContract.parentContractId) ?? null
      : null;
  const childVariations = selectedContract
    ? contracts.filter(
        (c) => c.contractType === 'variation' && c.parentContractId === selectedContract.id
      )
    : [];
  const hasLinkedAta = !!parentContract || childVariations.length > 0;

  const versionBadge = selectedContract ? contractVersionBadge(selectedContract) : null;
  const draftBadge = selectedContract ? draftStatusBadge(selectedContract.draftStatus, t) : null;

  const renderAttestation = (status?: string) => (
    <span className={`badge bg-${attestationVariant(status)}`}>
      {status === 'approved'
        ? t('contracts.attestation.approved', 'Godkänd')
        : status === 'rejected'
          ? t('contracts.attestation.rejected', 'Avvisad')
          : t('contracts.attestation.notAttested', 'Ej attesterad')}
    </span>
  );

  return (
    <>
      <ContractList
        className="mb-4"
        rows={rows}
        selectedId={selectedId}
        onSelect={setSelectedId}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyState={
          <div className="text-center py-4 text-muted">
            <i className="feather-file-text fs-1 d-block mb-3"></i>
            <h6 className="fw-bold mb-2">
              {t('providerContract.list.emptyTitle', 'Inga kontrakt ännu')}
            </h6>
            <p className="mb-0">
              {t(
                'providerContract.list.emptyDesc',
                'Kontrakten visas här när projektet har kontrakt.'
              )}
            </p>
          </div>
        }
      />

      {selectedContract && (
        <>
          {/* Summary */}
          <Card
            className="mb-4"
            title={`${t('providerContract.details.title', 'Kontraktsdetaljer')} #${selectedContract.contractNumber}`}
            icon="file"
          >
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span
                className={`badge bg-${selectedContract.contractType === 'variation' ? 'warning text-dark' : 'light text-dark border'}`}
              >
                {typeLabelFor(selectedContract)}
              </span>
              {versionBadge && (
                <span className={`badge bg-${versionBadge.variant}`}>{versionBadge.label}</span>
              )}
              {draftBadge && (
                <span className={`badge bg-${draftBadge.variant}`}>{draftBadge.label}</span>
              )}
            </div>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label text-muted small">
                  {t('contracts.descriptions.contractNameTitle')}
                </label>
                <div className="text-break">{selectedContract.contractName || t('contracts.list.contractName', { number: selectedContract.contractNumber })}</div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small">
                  {t('contracts.columns.contractAmount', 'Kontraktsumma')}
                </label>
                <div className="fw-semibold">{formatAmount(selectedContract.contractAmount)} kr</div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small">
                  {t('contracts.columns.paidAmount', 'Insatt')}
                </label>
                <div>{formatAmount(selectedContract.paidAmount)} kr</div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small">
                  {t('contracts.columns.remainingAmount', 'Kvarstående')}
                </label>
                <div>{formatAmount(selectedContract.remainingAmount)} kr</div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small">
                  {t('providerContract.details.companyAttestation', 'Hantverkare')}
                </label>
                <div>{renderAttestation(selectedContract.companyAttestation)}</div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small">
                  {t('providerContract.details.personAttestation', 'Beställare')}
                </label>
                <div>{renderAttestation(selectedContract.personAttestation)}</div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small">
                  {t('contracts.draftStatus.label', 'Utkast Status')}
                </label>
                <div>
                  {draftBadge ? (
                    <span className={`badge bg-${draftBadge.variant}`}>{draftBadge.label}</span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small">
                  {t('providerContract.details.companySigned', 'Signerat av hantverkare')}
                </label>
                <div>
                  {selectedContract.companySigned ? (
                    <span className="badge bg-success">{t('common.yes', 'Ja')}</span>
                  ) : (
                    <span className="badge bg-secondary">{t('common.no', 'Nej')}</span>
                  )}
                  {selectedContract.companySignedAt && (
                    <small className="text-muted ms-2">
                      {formatDate(selectedContract.companySignedAt)}
                    </small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small">
                  {t('providerContract.details.personSigned', 'Signerat av beställare')}
                </label>
                <div>
                  {selectedContract.personSigned ? (
                    <span className="badge bg-success">{t('common.yes', 'Ja')}</span>
                  ) : (
                    <span className="badge bg-secondary">{t('common.no', 'Nej')}</span>
                  )}
                  {selectedContract.personSignedAt && (
                    <small className="text-muted ms-2">
                      {formatDate(selectedContract.personSignedAt)}
                    </small>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Agreement text (read-only) */}
          <Card className="mb-4" title={t('providerContract.avtal.title', 'Avtalstext')} icon="file-text">
            {descriptions && (descriptions.assignment.replace(/<[^>]*>/g, '').trim() || descriptions.other.replace(/<[^>]*>/g, '').trim() || descriptions.referToAttachments) ? (
              <>
                {descriptions.assignment.replace(/<[^>]*>/g, '').trim() && (
                  <div className="mb-3">
                    <label className="form-label text-muted small">
                      {t('providerContract.avtal.assignment', 'Uppdragsbeskrivning')}
                    </label>
                    <div
                      className="text-break"
                      dangerouslySetInnerHTML={{ __html: descriptions.assignment }}
                    />
                  </div>
                )}
                {descriptions.referToAttachments && (
                  <p className="text-muted mb-3">
                    <i className="feather-paperclip me-1"></i>
                    {t('providerContract.avtal.seeAttachments', 'Se bilagor')}
                  </p>
                )}
                {descriptions.other.replace(/<[^>]*>/g, '').trim() && (
                  <div className="mb-0">
                    <label className="form-label text-muted small">
                      {t('providerContract.avtal.other', 'Övrigt')}
                    </label>
                    <div
                      className="text-break"
                      dangerouslySetInnerHTML={{ __html: descriptions.other }}
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted mb-0">
                {t('providerContract.avtal.empty', 'Ingen avtalstext har angetts.')}
              </p>
            )}
          </Card>

          {/* Linked ÄTA */}
          {hasLinkedAta && (
            <Card className="mb-4" title={t('providerContract.ata.title', 'Kopplade ÄTA')} icon="git-branch">
              {parentContract && (
                <div className="mb-3">
                  <label className="form-label text-muted small">
                    {t('providerContract.ata.parent', 'Ursprungskontrakt')}
                  </label>
                  <div>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => setSelectedId(parentContract.id)}
                    >
                      #{parentContract.contractNumber} – {contractTitle(parentContract)}
                    </button>
                  </div>
                </div>
              )}
              {childVariations.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <tbody>
                      {childVariations.map((ata) => (
                        <tr
                          key={ata.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedId(ata.id)}
                        >
                          <td>#{ata.contractNumber}</td>
                          <td>
                            <span className="badge bg-warning text-dark">{typeLabelFor(ata)}</span>
                          </td>
                          <td className="text-break">{contractTitle(ata)}</td>
                          <td className="text-nowrap">{formatAmount(ata.contractAmount)} kr</td>
                          <td>
                            {draftStatusBadge(ata.draftStatus, t) && (
                              <span
                                className={`badge bg-${draftStatusBadge(ata.draftStatus, t)!.variant}`}
                              >
                                {draftStatusBadge(ata.draftStatus, t)!.label}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Attachments (read-only) */}
          <Card className="mb-4" title={t('contracts.attachments.title', 'Bilagor')} icon="paperclip">
            {attachments.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="feather-paperclip fs-1 d-block mb-2"></i>
                <p className="mb-0">
                  {t('contracts.attachments.noAttachments', 'Inga bilagor tillagda ännu')}
                </p>
              </div>
            ) : (
              <div>
                {attachments.map((doc) => (
                  <div key={doc.id} className="border rounded p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <button
                          type="button"
                          className="btn btn-link fw-bold text-start text-break p-0 mb-1 text-decoration-none"
                          onClick={() => setAttachmentDetailsTarget(doc)}
                          title={t('contracts.attachments.viewDetails', 'Visa filinformation')}
                        >
                          <i className="feather-file me-2"></i>
                          {doc.originalName}
                        </button>
                        <div className="text-muted small">
                          <span className="me-3">
                            <i className="feather-user me-1"></i>
                            {t('contracts.attachments.uploadedBy', 'Uppladdad av')}{' '}
                            {doc.uploadedByName}
                          </span>
                          <span className="me-3">
                            <i className="feather-calendar me-1"></i>
                            {formatDate(doc.createdAt)}
                          </span>
                          <span>
                            <i className="feather-hard-drive me-1"></i>
                            {formatFileSize(doc.size)}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0 ms-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => preview.openPreview(doc)}
                          title={t('contracts.attachments.preview', 'Förhandsgranska')}
                          aria-label={t('contracts.attachments.preview', 'Förhandsgranska')}
                        >
                          <i className="feather-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => documentApi.downloadFile(doc.id, doc.originalName)}
                          title={t('contracts.attachments.download', 'Ladda ner')}
                          aria-label={t('contracts.attachments.download', 'Ladda ner')}
                        >
                          <i className="feather-download"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Event history (read-only) */}
          <ContractHistory
            entries={history}
            contractLabel={`${t('providerContract.details.title', 'Kontraktsdetaljer')} #${selectedContract.contractNumber}`}
          />

          {/* Milestones (read-only) */}
          <Card className="mb-4" title={t('providerContract.milestones.title', 'Etappmål')} icon="flag">
            {milestones.length === 0 ? (
              <div className="text-center py-3 text-muted">
                <small>
                  {t('providerContract.milestones.none', 'Inga etappmål för detta kontrakt.')}
                </small>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('providerContract.milestones.desc', 'Beskrivning')}</th>
                      <th>{t('providerContract.milestones.amount', 'Belopp')}</th>
                      <th>{t('providerContract.milestones.end', 'Slutdatum')}</th>
                      <th>{t('providerContract.details.companyAttestation', 'Hantverkare')}</th>
                      <th>{t('providerContract.details.personAttestation', 'Beställare')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {milestones.map((ms) => (
                      <tr key={ms.id}>
                        <td>{ms.milestoneNumber}</td>
                        <td className="text-break">{ms.description}</td>
                        <td className="text-nowrap">{formatAmount(ms.amount)} kr</td>
                        <td>{formatDate(ms.endDate)}</td>
                        <td>{renderAttestation(ms.companyAttestation)}</td>
                        <td>{renderAttestation(ms.personAttestation)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <DocumentPreviewModal
        previewDoc={preview.previewDoc}
        previewUrl={preview.previewUrl}
        previewHtml={preview.previewHtml}
        previewLoading={preview.previewLoading}
        previewError={preview.previewError}
        onClose={preview.closePreview}
      />

      {attachmentDetailsTarget && (
        <DocumentIntegrityModal
          document={attachmentDetailsTarget}
          onClose={() => setAttachmentDetailsTarget(null)}
        />
      )}
    </>
  );
}
